/** Merit into rank, and the perk choices that promotions bring (02 §11, 04 §4.2). */

import { Pcg32 } from '../../sim/rng';
import { PERK_CHOICES, PERK_FAMILIES, PERK_RANKS, PERKS } from '../content/perks';
import { CAMPAIGN_ONLY_RANK, RANK_THRESHOLDS } from '../content/ranks';
import type { PerkId, RangerRecord } from '../types';
import { shuffled } from '../util';

/** Rank index for cumulative merit. COL also needs a won Operation (`campaignDone`). */
export function rankFor(merit: number, campaignDone: boolean): number {
  let rank = 0;
  RANK_THRESHOLDS.forEach((threshold, i) => {
    if (merit >= threshold) rank = i;
  });
  return rank >= CAMPAIGN_ONLY_RANK && !campaignDone ? CAMPAIGN_ONLY_RANK - 1 : rank;
}

/** Three distinct perks the ranger lacks, from different families where possible; seeded by ranger and `salt`. */
export function drawPerkChoice(ranger: RangerRecord, salt: string, avoid: readonly PerkId[] = []): PerkId[] {
  const rng = new Pcg32(ranger.seed, 0x9e4c).fork(salt);
  const open = PERKS.filter((p) => !ranger.perks.includes(p.id) && !avoid.includes(p.id));
  const choice: PerkId[] = [];
  for (const family of shuffled(rng, PERK_FAMILIES)) {
    const inFamily = open.filter((p) => p.family === family);
    if (inFamily.length > 0 && choice.length < PERK_CHOICES) choice.push(rng.pick(inFamily).id);
  }
  for (const perk of shuffled(rng, open)) {
    if (choice.length >= PERK_CHOICES) break;
    if (!choice.includes(perk.id)) choice.push(perk.id);
  }
  return choice;
}

/**
 * Adds merit, promotes, and queues a perk choice for each perk rank passed (Operation mode
 * only - Classic has rank and medals but no perks). Returns the new rank, or null.
 */
export function applyMerit(ranger: RangerRecord, merit: number, perks: boolean, campaignDone: boolean): number | null {
  ranger.merit += merit;
  const before = ranger.rank;
  const after = Math.max(before, rankFor(ranger.merit, campaignDone));
  if (after === before) return null;
  for (let rank = before + 1; rank <= after; rank++) {
    if (!perks || !PERK_RANKS.includes(rank)) continue;
    const choice = drawPerkChoice(ranger, `rank-${rank}`);
    if (choice.length > 0) ranger.pendingPerkChoices.push(choice);
  }
  ranger.rank = after;
  return after;
}

/** He now holds `perk`: owed choices that still offer it get a replacement card instead. */
export function redealChoices(ranger: RangerRecord, perk: PerkId): void {
  ranger.pendingPerkChoices = ranger.pendingPerkChoices.map((choice, i) => {
    if (!choice.includes(perk)) return choice;
    const kept = choice.filter((p) => p !== perk);
    const spare = drawPerkChoice(ranger, `swap-${ranger.perks.length}-${i}`, kept)[0];
    return spare ? [...kept, spare] : kept;
  });
}

/** Resolve the oldest owed perk choice. Later choices that offered the same perk get a replacement. */
export function takePerk(ranger: RangerRecord, perk: PerkId): void {
  const owed = ranger.pendingPerkChoices[0];
  if (!owed) throw new Error('No perk choice is owed to this ranger.');
  const offered = owed.includes(perk) && !ranger.perks.includes(perk);
  if (!offered) throw new Error('That perk is not among the choices offered.');
  ranger.perks.push(perk);
  ranger.pendingPerkChoices.shift();
  redealChoices(ranger, perk);
}
